/* Chart renderers for the Market Snapshot post.
 *
 * These build DOM imperatively rather than in JSX on purpose: the geometry is
 * derived from the data at mount time, and keeping one implementation means the
 * post and the standalone HTML we hand out cannot drift apart. Change a number
 * in data.ts and every axis, label and tile thumbnail moves with it.
 *
 * Six figures, six index thumbnails. Each figure takes the shape of its own
 * argument rather than defaulting to a line that rises: a tension, a break, a
 * crossing, a spread, a mismatch, a fall away from a norm.
 *
 * Called once from a client component. Reads nothing but its arguments.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

const C: Record<string, string> = {
  blue: "#056CC1", amber: "#F2AC3C", red: "#DF1E1E", black: "#111111",
  neutral: "rgba(0,0,0,0.16)"
};
const INK: Record<string, string> = {
  blue: "#056CC1", amber: "#9a6c00", red: "#DF1E1E", black: "#111111",
  neutral: "rgba(0,0,0,0.55)"
};
const PLOT = "#E8E3D9";
const MONO = "'DM Mono',monospace";
const SANS = "var(--font-sans)";

function el(t: string, a?: any, k?: any[]): any {
  const n = document.createElement(t);
  if (a) for (const x in a) {
    if (x === "html") n.innerHTML = String(a[x]);
    else if (x === "text") n.textContent = String(a[x]);
    else n.setAttribute(x, String(a[x]));
  }
  (k || []).forEach(function (c: any) { if (c) n.appendChild(c); });
  return n;
}
function sv(t: string, a: any): any {
  const n = document.createElementNS("http://www.w3.org/2000/svg", t);
  for (const x in a) n.setAttribute(x, String(a[x]));
  return n;
}
function fmt(n: any, d?: number) {
  return Number(n).toLocaleString("en-US",
    { minimumFractionDigits: d || 0, maximumFractionDigits: d || 0 });
}
/** A plot canvas that stretches to its container but keeps its own proportions. */
function canvas(w: number, h: number, aria: string) {
  return sv("svg", {
    viewBox: "0 0 " + w + " " + h, width: "100%", preserveAspectRatio: "none",
    style: "display:block;height:auto;overflow:visible", role: "img", "aria-label": aria
  });
}
function txt(x: number, y: number, s: string, o?: any) {
  o = o || {};
  const t = sv("text", {
    x: x.toFixed(1), y: y.toFixed(1), "text-anchor": o.anchor || "start",
    "font-family": o.family || MONO, "font-size": o.size || 12,
    fill: o.fill || "rgba(0,0,0,.62)",
    ...(o.weight ? { "font-weight": o.weight } : {}),
    ...(o.spacing ? { "letter-spacing": o.spacing } : {}),
    ...(o.halo === false ? {} : {
      stroke: o.halo || PLOT, "stroke-width": 4, "paint-order": "stroke",
      "stroke-linejoin": "round"
    })
  });
  t.textContent = s;
  return t;
}
function line(pts: [number, number][]) {
  return pts.map(function (p, i) {
    return (i ? "L" : "M") + p[0].toFixed(1) + " " + p[1].toFixed(1);
  }).join(" ");
}
function gridlines(w: number, h: number, pl: number, pr: number, pt: number, pb: number, n?: number) {
  const g = sv("g", {});
  const rows = n || 3;
  for (let i = 1; i <= rows; i++) {
    const y = pt + (h - pt - pb) * i / (rows + 1);
    g.appendChild(sv("line", {
      x1: pl, x2: w - pr, y1: y.toFixed(1), y2: y.toFixed(1),
      stroke: "rgba(0,0,0,0.12)", "stroke-width": 1, "stroke-dasharray": "3 6"
    }));
  }
  return g;
}

export function mountFigures(root: HTMLElement, D: any, tt: HTMLElement) {

  function tip(node: any, html: string) {
    node.addEventListener("mouseenter", function () { tt.innerHTML = html; tt.style.opacity = "1"; });
    node.addEventListener("mousemove", function (e: any) {
      let x = e.clientX + 14, y = e.clientY + 14;
      if (x + 290 > window.innerWidth) x = e.clientX - 290;
      if (y + 90 > window.innerHeight) y = e.clientY - 90;
      tt.style.left = x + "px"; tt.style.top = y + "px";
    });
    node.addEventListener("mouseleave", function () { tt.style.opacity = "0"; });
  }
  function flabel(left: string, right?: string) {
    return el("div", { "class": "flabel" },
      [el("span", { text: left }), right ? el("span", { text: right }) : null]);
  }
  function ticks(labels: string[], every: number, pl?: number, pr?: number, w?: number) {
    const keep = labels.filter(function (_l, i) { return i % every === 0 || i === labels.length - 1; });
    const d = el("div", { "class": "ticks" });
    if (pl !== undefined) {
      d.setAttribute("style", "padding-left:" + (100 * pl / (w || 1060)).toFixed(2) +
        "%;padding-right:" + (100 * (pr || 0) / (w || 1060)).toFixed(2) + "%");
    }
    keep.forEach(function (l) { d.appendChild(el("span", { text: l })); });
    return d;
  }
  function table(head: string[], rows: any[][], note?: string) {
    const d = el("details", { "class": "tbl" });
    d.appendChild(el("summary", { text: "Table view" }));
    const tb = el("table"), tr = el("tr");
    head.forEach(function (h) { tr.appendChild(el("th", { text: h })); });
    tb.appendChild(el("thead", null, [tr]));
    const body = el("tbody");
    rows.forEach(function (r) {
      const x = el("tr");
      r.forEach(function (c) { x.appendChild(el("td", { text: String(c) })); });
      body.appendChild(x);
    });
    tb.appendChild(body); d.appendChild(tb);
    if (note) d.appendChild(el("div", { "class": "tnote", text: note }));
    return d;
  }
  function plot(node: any, dark?: boolean) {
    return el("div", { "class": "plot" + (dark ? " dark" : "") }, [node]);
  }

  /* ── 1 · company split, diverging from a centre line ─────────────────
     Grew rises, Shrank falls, and the two that did neither straddle the axis.
     The tension is one small blue bar against one big red one with a neutral
     mass between them going nowhere. */
  function figCompanySplit(d: any) {
    const W = 1060, H = 210, mid = 92;
    const ups = d.buckets.filter((b: any) => b.side === "up").map((b: any) => b.pct);
    const mids = d.buckets.filter((b: any) => b.side === "mid").map((b: any) => b.pct / 2);
    const dns = d.buckets.filter((b: any) => b.side === "down").map((b: any) => b.pct);
    const up = Math.max.apply(null, ups.concat(mids));
    const dn = Math.max.apply(null, dns.concat(mids));
    const unit = Math.min((mid - 40) / up, (H - mid - 46) / dn);
    const bw = 182, gap = 34;
    let x = (W - (d.buckets.length * bw + (d.buckets.length - 1) * gap)) / 2 - 92;
    const s = canvas(W, H, "What the tracked Czech tech employers did with their tech team over the year");
    s.appendChild(sv("line", { x1: 0, x2: W, y1: mid, y2: mid, stroke: "rgba(0,0,0,.35)", "stroke-width": 1 }));
    d.buckets.forEach(function (b: any) {
      const h = b.pct * unit;
      const y = b.side === "up" ? mid - h : b.side === "down" ? mid : mid - h / 2;
      const top = b.side === "down" ? mid + h : y;
      const r = sv("rect", {
        x: x.toFixed(1), y: y.toFixed(1), width: bw, height: h.toFixed(1), rx: 3,
        fill: C[b.tone] || C.neutral
      });
      tip(r, "<b>" + b.label + "</b><br>" + fmt(b.firms) + " companies · " + b.pct + "%");
      s.appendChild(r);
      const ly = b.side === "down" ? top + 22 : top - 12;
      s.appendChild(txt(x + bw / 2, ly, b.pct + "%",
        { anchor: "middle", size: 17, family: SANS, weight: 800, fill: "rgba(0,0,0,.78)" }));
      s.appendChild(txt(x + bw / 2, b.side === "down" ? ly + 15 : ly - 15,
        b.label + " · " + b.firms, { anchor: "middle", size: 10, fill: "rgba(0,0,0,.45)" }));
      x += bw + gap;
    });
    s.appendChild(txt(W - 4, mid - 9, "GREW", { anchor: "end", size: 11, fill: C.blue, spacing: "0.14em" }));
    s.appendChild(txt(W - 4, mid + 19, "SHRANK", { anchor: "end", size: 11, fill: C.red, spacing: "0.14em" }));

    const funnel = el("div", { "class": "funnel" });
    d.funnel.forEach(function (f: any, i: number) {
      if (i) funnel.appendChild(el("span", { "class": "farrow", html: "&rarr;" }));
      funnel.appendChild(el("span", { "class": "fstep" },
        [el("b", { text: f.value }), el("span", { text: f.note })]));
    });
    return [funnel, flabel(d.label, d.note), plot(s),
      table(["What they did", "Companies", "Share"],
        d.buckets.map((b: any) => [b.label, fmt(b.firms), b.pct + "%"]))];
  }

  /* ── 2 · a decade flat, then a break ─────────────────────────────────
     The axis starts at zero and the annotations do the work. Ten years of flat
     is the argument; a truncated axis would fake the drama instead. */
  function figExitMonths(d: any) {
    const W = 1060, H = 300, PL = 44, PR = 60, PT = 46, PB = 26;
    const lo = 0, hi = 36, n = d.series.length;
    const X = d.series.map((_s: any, i: number) => PL + i * (W - PL - PR) / (n - 1));
    const yy = (v: number) => PT + (hi - v) / (hi - lo) * (H - PT - PB);
    const pts: [number, number][] = d.series.map((s: any, i: number) => [X[i], yy(s.value)]);
    const iFlat = d.series.findIndex((s: any) => s.year === d.flatUntil);
    const iPart = d.series.findIndex((s: any) => s.year === d.partialFrom);
    const s = canvas(W, H, "Median months in a job before leaving, by year of leaving, 2015 to mid 2026");
    s.appendChild(gridlines(W, H, PL, PR, PT, PB));
    [12, 24, 36].forEach(function (v) {
      s.appendChild(txt(PL - 10, yy(v) + 4, String(v),
        { anchor: "end", fill: "rgba(0,0,0,0.40)", halo: false }));
    });
    s.appendChild(sv("rect", {
      x: (X[0] - 6).toFixed(1), y: PT - 6, width: (X[iFlat] - X[0] + 12).toFixed(1),
      height: H - PT - PB + 10, fill: "rgba(0,0,0,0.035)"
    }));
    s.appendChild(txt((X[0] + X[iFlat]) / 2, H - PB - 26, "HELD NEAR TWO YEARS FOR A DECADE",
      { anchor: "middle", fill: "rgba(0,0,0,.45)", spacing: "0.14em", halo: false }));
    const bx = (X[iFlat] + X[iFlat + 1]) / 2;
    s.appendChild(sv("line", {
      x1: bx.toFixed(1), x2: bx.toFixed(1), y1: PT - 6, y2: H - PB + 6,
      stroke: C.red, "stroke-width": 1.4, "stroke-dasharray": "5 4", opacity: 0.55
    }));
    s.appendChild(txt(bx + 12, H - PB - 26, "THEN IT BROKE",
      { fill: C.red, spacing: "0.14em", halo: false }));
    const px = (X[iPart - 1] + X[iPart]) / 2;
    s.appendChild(sv("rect", {
      x: px.toFixed(1), y: PT - 6, width: (W - PR - px + 8).toFixed(1),
      height: H - PT - PB + 10, fill: "rgba(0,0,0,0.05)"
    }));
    s.appendChild(txt(W - PR + 4, H - PB - 8, d.partialFrom + " PARTIAL",
      { anchor: "end", size: 11, fill: "rgba(0,0,0,.38)", spacing: "0.14em", halo: false }));
    s.appendChild(sv("path", {
      d: line(pts), fill: "none", stroke: C.blue, "stroke-width": 3, "stroke-linejoin": "round"
    }));
    pts.forEach(function (p, i) {
      const edge = i === 0 || i === n - 1;
      s.appendChild(sv("circle", {
        cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: edge ? 5 : 3.6,
        fill: i === n - 1 ? C.amber : C.blue, opacity: edge ? 1 : 0.55
      }));
      const hit = sv("circle", { cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 18, fill: "transparent" });
      tip(hit, "<b>" + d.series[i].year + "</b><br>" + d.series[i].value + " months in a job before leaving" +
        (d.series[i].year === d.partialFrom ? "<br>partial year" : ""));
      s.appendChild(hit);
    });
    s.appendChild(txt(pts[0][0] + 10, pts[0][1] - 12, d.series[0].value + " months", { size: 13 }));
    s.appendChild(txt(pts[n - 1][0] - 10, pts[n - 1][1] - 14, d.series[n - 1].value + " months",
      { anchor: "end", size: 15, family: SANS, weight: 800, fill: "rgba(0,0,0,.75)" }));
    return [flabel(d.label), plot(s),
      ticks(d.series.map((x: any) => x.year), 1, PL, PR, W),
      table(["Year of leaving", "Months in a job"],
        d.series.map((x: any) => [x.year, x.value]),
        "2026 covers the first half of the year only.")];
  }

  /* ── 3 · the stack turned ────────────────────────────────────────────
     Colour carries direction rather than language, so the shift reads before
     a single label does. */
  function figLanguages(d: any) {
    const W = 1060, H = 320, PL = 44, PR = 168, PT = 26, PB = 22;
    const lo = 0, hi = 20, n = d.years.length;
    const X = d.years.map((_y: string, i: number) => PL + i * (W - PL - PR) / (n - 1));
    const yy = (v: number) => PT + (hi - v) / (hi - lo) * (H - PT - PB);
    const s = canvas(W, H, "Share of active public repositories by main language, 2018 to 2025");
    s.appendChild(gridlines(W, H, PL, PR, PT, PB));
    [0, 10, 20].forEach(function (v) {
      s.appendChild(txt(PL - 10, yy(v) + 4, v + "%",
        { anchor: "end", fill: "rgba(0,0,0,0.40)", halo: false }));
    });
    const ci = d.years.indexOf(d.crossover), cx = X[ci];
    s.appendChild(sv("line", {
      x1: cx.toFixed(1), x2: cx.toFixed(1), y1: PT - 4, y2: H - PB,
      stroke: "rgba(0,0,0,.28)", "stroke-width": 1.4, "stroke-dasharray": "5 4"
    }));
    s.appendChild(txt(cx - 10, PT + 10, "THE STACK TURNED HERE",
      { anchor: "end", fill: "rgba(0,0,0,.5)", spacing: "0.14em", halo: false }));
    d.series.forEach(function (sr: any) {
      const pts: [number, number][] = sr.values.map((v: number, i: number) => [X[i], yy(v)]);
      s.appendChild(sv("path", {
        d: line(pts), fill: "none", stroke: C[sr.tone], "stroke-width": 2.6,
        opacity: sr.weight, "stroke-linejoin": "round"
      }));
      s.appendChild(sv("circle", {
        cx: pts[n - 1][0].toFixed(1), cy: pts[n - 1][1].toFixed(1), r: 4,
        fill: C[sr.tone], opacity: sr.weight
      }));
      pts.forEach(function (p, i) {
        const hit = sv("circle", { cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 13, fill: "transparent" });
        tip(hit, "<b>" + sr.label + " · " + d.years[i] + "</b><br>" + sr.values[i] +
          "% of active public repositories");
        s.appendChild(hit);
      });
      s.appendChild(txt(W - PR + 13, pts[n - 1][1] + 4,
        sr.label + " " + sr.values[n - 1] + "%",
        { size: 13, fill: C[sr.tone], halo: PLOT }));
    });
    return [flabel(d.label, "Blue rising · red falling"), plot(s),
      ticks(d.years, 1, PL, PR, W),
      table(["Year"].concat(d.series.map((x: any) => x.label)),
        d.years.map(function (y: string, i: number) {
          return [y].concat(d.series.map((x: any) => x.values[i] + "%"));
        }))];
  }

  /* ── 4 · the whole range lifted ──────────────────────────────────────
     The median is drawn heavy against a light band, so the point is that the
     spread moved with it and not only the middle. */
  function figScene(d: any) {
    const W = 1060, H = 205, PL = 44, PR = 130, PT = 26, PB = 20;
    const lo = 0, hi = 320, n = d.median.length;
    const X = d.quarters.map((_q: string, i: number) => PL + i * (W - PL - PR) / (n - 1));
    const yy = (v: number) => PT + (hi - v) / (hi - lo) * (H - PT - PB);
    const med: [number, number][] = d.median.map((v: number, i: number) => [X[i], yy(v)]);
    const up: [number, number][] = d.p75.map((v: number, i: number) => [X[i], yy(v)]);
    const dn: [number, number][] = d.p25.map((v: number, i: number) => [X[i], yy(v)]);
    const s = canvas(W, H, "Public contributions per quarter among a fixed group of active contributors");
    s.appendChild(gridlines(W, H, PL, PR, PT, PB));
    [100, 200, 300].forEach(function (v) {
      s.appendChild(txt(PL - 9, yy(v) + 4, String(v),
        { anchor: "end", size: 11, fill: "rgba(0,0,0,0.40)", halo: false }));
    });
    const band = line(up) + " " +
      dn.slice().reverse().map((p) => "L" + p[0].toFixed(1) + " " + p[1].toFixed(1)).join(" ") + " Z";
    s.appendChild(sv("path", { d: band, fill: C.blue, opacity: 0.09 }));
    s.appendChild(sv("path", { d: line(up), fill: "none", stroke: C.blue, "stroke-width": 1, opacity: 0.3 }));
    s.appendChild(sv("path", { d: line(dn), fill: "none", stroke: C.blue, "stroke-width": 1, opacity: 0.3 }));
    s.appendChild(sv("path", {
      d: line(med), fill: "none", stroke: C.blue, "stroke-width": 3.4, "stroke-linejoin": "round"
    }));
    med.forEach(function (p, i) {
      s.appendChild(sv("circle", {
        cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: i === n - 1 ? 5 : 3.6,
        fill: i === n - 1 ? C.amber : C.blue
      }));
      const hit = sv("circle", { cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 18, fill: "transparent" });
      tip(hit, "<b>" + d.quarters[i] + "</b><br>median " + d.median[i] + " contributions<br>" +
        "middle half " + d.p25[i] + " to " + d.p75[i]);
      s.appendChild(hit);
    });
    s.appendChild(txt(W - PR + 9, med[n - 1][1] + 4, "median " + d.median[n - 1],
      { fill: "rgba(0,0,0,.75)" }));
    s.appendChild(txt(W - PR + 9, up[n - 1][1] + 4, "top quarter", { size: 11, fill: "rgba(0,0,0,.4)" }));
    s.appendChild(txt(W - PR + 9, dn[n - 1][1] + 4, "bottom quarter", { size: 11, fill: "rgba(0,0,0,.4)" }));
    s.appendChild(txt(PL + 6, PT + 10, "THE WHOLE RANGE LIFTED, NOT JUST THE TOP",
      { size: 10, fill: "rgba(0,0,0,.42)", spacing: "0.12em", halo: false }));
    return [flabel(d.label), plot(s), ticks(d.quarters, 1, PL, PR, W),
      table(["Quarter", "Bottom quarter", "Median", "Top quarter"],
        d.quarters.map((q: string, i: number) => [q, d.p25[i], d.median[i], d.p75[i]]))];
  }

  /* ── 5 · small share, loud voice ─────────────────────────────────────
     The mismatch is the finding, so the two bars lead and the trend line is
     demoted to a small panel underneath. */
  function figAi(d: any) {
    const W = 1060, H = 230, PL = 90, PT = 40, PB = 34;
    const unit = (H - PT - PB) / 12;
    const bw = 230, gap = 130;
    const s = canvas(W, H, "AI and ML share of active repositories against share of stars");
    s.appendChild(sv("line", {
      x1: PL - 24, x2: W - 20, y1: H - PB, y2: H - PB, stroke: "rgba(0,0,0,.28)", "stroke-width": 1
    }));
    let x = PL;
    d.prominence.forEach(function (b: any) {
      const h = b.value * unit;
      const r = sv("rect", {
        x: x.toFixed(1), y: (H - PB - h).toFixed(1), width: bw, height: h.toFixed(1), rx: 4,
        fill: C[b.tone] || C.neutral
      });
      tip(r, "<b>" + b.label + "</b><br>" + b.value + "%<br>" +
        fmt(d.avgStars.aiml, 1) + " stars per AI project against " + fmt(d.avgStars.other, 1) + " for the rest");
      s.appendChild(r);
      s.appendChild(txt(x + bw / 2, H - PB - h - 14, b.value.toFixed(1) + "%",
        { anchor: "middle", size: 26, family: SANS, weight: 800, fill: "rgba(0,0,0,.8)" }));
      s.appendChild(txt(x + bw / 2, H - PB + 20, b.label, { anchor: "middle", fill: "rgba(0,0,0,.5)" }));
      x += bw + gap;
    });
    const topY = H - PB - d.prominence[1].value * unit;
    s.appendChild(txt(W - 30, topY - 24, "NEARLY THREE TIMES",
      { anchor: "end", fill: C.red, spacing: "0.14em", halo: false }));
    s.appendChild(txt(W - 30, topY - 6, "THE ATTENTION PER PROJECT",
      { anchor: "end", fill: C.red, spacing: "0.14em", halo: false }));

    const w2 = 1060, h2 = 110, pl2 = 40, pr2 = 54, pt2 = 18, pb2 = 16;
    const m = d.share.length;
    const X2 = d.share.map((_v: number, i: number) => pl2 + i * (w2 - pl2 - pr2) / (m - 1));
    const y2 = (v: number) => pt2 + (6 - v) / 6 * (h2 - pt2 - pb2);
    const p2: [number, number][] = d.share.map((v: number, i: number) => [X2[i], y2(v)]);
    const s2 = canvas(w2, h2, "AI and ML share of active public repositories by year");
    s2.appendChild(gridlines(w2, h2, pl2, pr2, pt2, pb2, 1));
    [2, 4, 6].forEach(function (v) {
      s2.appendChild(txt(pl2 - 8, y2(v) + 4, v + "%",
        { anchor: "end", size: 10, fill: "rgba(0,0,0,0.38)", halo: false }));
    });
    s2.appendChild(sv("path", {
      d: line(p2), fill: "none", stroke: C.amber, "stroke-width": 2, "stroke-linejoin": "round"
    }));
    p2.forEach(function (p, i) {
      const hit = sv("circle", { cx: p[0].toFixed(1), cy: p[1].toFixed(1), r: 14, fill: "transparent" });
      tip(hit, "<b>" + d.years[i] + "</b><br>" + d.share[i] + "% of active repositories are AI or ML");
      s2.appendChild(hit);
    });
    s2.appendChild(sv("circle", {
      cx: p2[m - 1][0].toFixed(1), cy: p2[m - 1][1].toFixed(1), r: 4, fill: C.red
    }));
    s2.appendChild(txt(w2 - pr2 + 8, p2[m - 1][1] + 4, d.share[m - 1] + "%",
      { size: 11, fill: "rgba(0,0,0,.6)" }));
    const sub = el("div", { "class": "subfig" },
      [flabel(d.trendLabel), plot(s2), ticks(d.years, 2, pl2, pr2, w2)]);

    return [flabel(d.label), plot(s), sub,
      table(["Year", "AI and ML share of active repositories"],
        d.years.map((y: string, i: number) => [y, d.share[i] + "%"]),
        "Identified by keyword, which undercounts them, so the share is a floor.")];
  }

  /* ── 6 · juniors falling away from a norm ────────────────────────────
     A reference line at the starting level, so the drop reads as falling below
     a norm rather than as bars simply getting shorter. */
  function figJuniorShare(d: any) {
    const W = 1060, H = 185, PL = 40, PR = 16, PT = 34, PB = 18;
    const hi = 30, n = d.values.length, gap = 12;
    const bw = (W - PL - PR - gap * (n - 1)) / n;
    const yy = (v: number) => H - PB - (v / hi) * (H - PT - PB);
    const s = canvas(W, H, "Share of active tracked developers within three years of their first job");
    [10, 20].forEach(function (v) {
      s.appendChild(txt(PL - 9, yy(v) + 4, v + "%",
        { anchor: "end", size: 11, fill: "rgba(0,0,0,0.40)", halo: false }));
    });
    const ry = yy(d.reference.value);
    s.appendChild(sv("line", {
      x1: PL, x2: W - PR, y1: ry.toFixed(1), y2: ry.toFixed(1),
      stroke: "rgba(0,0,0,.30)", "stroke-width": 1, "stroke-dasharray": "5 4"
    }));
    s.appendChild(txt(W - PR, ry - 8, d.reference.label,
      { anchor: "end", size: 10, fill: "rgba(0,0,0,.42)", spacing: "0.12em", halo: false }));
    const iPart = d.years.indexOf(d.partialFrom);
    d.values.forEach(function (v: number, i: number) {
      const x = PL + i * (bw + gap), y = yy(v), recent = i >= n - 2;
      const r = sv("rect", {
        x: x.toFixed(1), y: y.toFixed(1), width: bw.toFixed(1), height: (H - PB - y).toFixed(1), rx: 3,
        fill: recent ? C.amber : "rgba(0,0,0,0.18)", opacity: i === iPart ? 0.5 : 1
      });
      tip(r, "<b>" + d.years[i] + "</b><br>" + v + "% of active developers are within three years of " +
        "their first job" + (i === iPart ? "<br>partial year" : ""));
      s.appendChild(r);
      s.appendChild(txt(x + bw / 2, y + 21, v + "%",
        { anchor: "middle", size: 14, family: SANS, weight: 800, halo: false,
          fill: recent ? "rgba(0,0,0,.72)" : "rgba(0,0,0,.55)" }));
    });
    return [flabel(d.label, d.partialFrom + " partial"), plot(s),
      ticks(d.years, 1, PL, PR, W),
      table(["Year", "Junior share of the active pool"],
        d.years.map((y: string, i: number) => [y, d.values[i] + "%"]),
        "A share, not a count, so changes in how many developers we track largely cancel out.")];
  }

  /* ══ mount ═════════════════════════════════════════════════════════ */
  function mount(id: string, nodes: any[]) {
    const f = root.querySelector("#" + id) as HTMLElement;
    if (!f) return;
    f.innerHTML = "";
    nodes.forEach(function (n: any) { if (n) f.appendChild(n); });
  }

  mount("fig-companies", figCompanySplit(D.companySplit));
  mount("fig-tenure", figExitMonths(D.exitMonths));
  mount("fig-stack", figLanguages(D.languages));
  mount("fig-scene", figScene(D.scene));
  mount("fig-ai", figAi(D.ai));
  mount("fig-entry", figJuniorShare(D.juniorShare));
}

/* Thumbnails for the finding index.
 *
 * Same rule as the figures: drawn from SNAPSHOT at mount time, so a data
 * refresh moves the tiles with the charts instead of leaving six little lies
 * above the report. Each `.thumb` names its source through `data-thumb`. */
export function mountTiles(root: HTMLElement, D: any) {
  const TW = 320, TH = 88;

  function frame(inner: string, h?: number) {
    const s = sv("svg", {
      viewBox: "0 0 " + TW + " " + (h || TH), width: "100%",
      style: "display:block;height:auto;overflow:visible",
      "aria-hidden": "true", focusable: "false"
    });
    s.innerHTML = inner;
    return s;
  }
  function spark(vals: number[], tone: string, fill?: boolean) {
    const lo = Math.min.apply(null, vals), hi = Math.max.apply(null, vals);
    const sp = (hi - lo) || 1;
    const pts: [number, number][] = vals.map(function (v, i) {
      return [i * TW / (vals.length - 1), 9 + (hi - v) / sp * (TH - 18)];
    });
    const d = line(pts);
    return frame(
      (fill === false ? "" :
        '<path d="' + d + " L" + TW + " " + TH + " L0 " + TH + ' Z" fill="' + tone + '" opacity=".14"/>') +
      '<path d="' + d + '" fill="none" stroke="' + tone + '" stroke-width="2.6" ' +
      'stroke-linejoin="round" stroke-linecap="round"/>' +
      '<circle cx="' + pts[pts.length - 1][0].toFixed(1) + '" cy="' + pts[pts.length - 1][1].toFixed(1) +
      '" r="4.5" fill="' + tone + '"/>');
  }
  /** One divided bar, not four shapes: a pill radius on a narrow slice turns
   *  the smallest share into a circle and reads bigger than it is. */
  function segments(buckets: any[]) {
    // A 34px bar in an 88px box leaves dead space above and below it, which
    // pushed the caption underneath a hand's width away from what it labels.
    const h = 34, box = 44, y = (box - h) / 2;
    let x = 0;
    const bars: string[] = [], rules: string[] = [];
    buckets.forEach(function (b: any, i: number) {
      const w = b.pct / 100 * TW;
      bars.push('<rect x="' + x.toFixed(1) + '" y="' + y + '" width="' + w.toFixed(1) +
        '" height="' + h + '" fill="' + (C[b.tone] || C.neutral) + '"/>');
      if (i) rules.push('<rect x="' + (x - 1.25).toFixed(1) + '" y="' + y +
        '" width="2.5" height="' + h + '" fill="#F2EEE6"/>');
      x += w;
    });
    return frame('<defs><clipPath id="al-bcl"><rect x="0" y="' + y + '" width="' + TW +
      '" height="' + h + '" rx="' + h / 2 + '"/></clipPath></defs>' +
      '<g clip-path="url(#al-bcl)">' + bars.join("") + rules.join("") + "</g>", box);
  }
  function columns(vals: number[], tone: string, onLast: number) {
    const hi = Math.max.apply(null, vals) * 1.05, n = vals.length, gap = 2.4;
    const w = (TW - gap * (n - 1)) / n;
    return frame(vals.map(function (v, i) {
      const h = Math.max(3, v / hi * TH);
      return '<rect x="' + (i * (w + gap)).toFixed(1) + '" y="' + (TH - h).toFixed(1) +
        '" width="' + w.toFixed(1) + '" height="' + h.toFixed(1) + '" rx="1.5" fill="' +
        (i >= n - onLast ? tone : "rgba(0,0,0,0.16)") + '"/>';
    }).join(""));
  }
  /** Five shares on one tiny panel. No label fits at this size, so colour does
   *  all the work: blue rises, red falls. */
  function lines(series: any[]) {
    return frame(series.map(function (sr: any) {
      const pts: [number, number][] = sr.values.map(function (v: number, i: number) {
        return [i * TW / (sr.values.length - 1), 8 + (20 - v) / 20 * (TH - 16)];
      });
      return '<path d="' + line(pts) + '" fill="none" stroke="' + C[sr.tone] +
        '" stroke-width="2.1" opacity="' + sr.weight + '" stroke-linejoin="round"/>';
    }).join(""));
  }

  const THUMBS: Record<string, () => any> = {
    companies: () => segments(D.companySplit.buckets),
    tenure: () => spark(D.exitMonths.series.map((s: any) => s.value), C.blue),
    stack: () => lines(D.languages.series),
    scene: () => spark(D.scene.median, C.blue),
    ai: () => spark(D.ai.share, C.amber),
    entry: () => columns(D.juniorShare.values, C.amber, 2)
  };

  Array.prototype.forEach.call(root.querySelectorAll("[data-thumb]"), function (n: HTMLElement) {
    const make = THUMBS[String(n.getAttribute("data-thumb"))];
    if (!make) return;
    n.innerHTML = "";
    n.appendChild(make());
  });
}

/* One canvas. Black on cream through an 8x8 ordered Bayer matrix: an even field
 * that climbs toward the edges, ridge contours that bend around the pointer, and
 * a soft opening under it. Returns a teardown function. */
export function mountRaster(cv: HTMLCanvasElement): () => void {

  var ctx=cv.getContext('2d') as CanvasRenderingContext2D, W=0,H=0, mx=0.5,my=0.35, queued=false;
  /* 8x8 ordered Bayer matrix */
  var B=[[0,32,8,40,2,34,10,42],[48,16,56,24,50,18,58,26],[12,44,4,36,14,46,6,38],
       [60,28,52,20,62,30,54,22],[3,35,11,43,1,33,9,41],[51,19,59,27,49,17,57,25],
       [15,47,7,39,13,45,5,37],[63,31,55,23,61,29,53,21]];
  function size(){ W=cv.width=Math.max(1,Math.ceil(window.innerWidth/3));
           H=cv.height=Math.max(1,Math.ceil(window.innerHeight/3)); }
  function draw(){
    queued=false;
    var img=ctx.createImageData(W,H), d=img.data;
    for(var y=0;y<H;y++){
    var ny=y/H;
    for(var x=0;x<W;x++){
      var nx=x/W, i=(y*W+x)*4;
      /* Base field: light in the upper middle, heavy toward the edges and the
         bottom. The climb above the light band is deliberately slower than the
         climb below it — the masthead sits in the top strip of the viewport,
         and a symmetric falloff put a near-solid checkerboard behind it. */
      var ex=Math.abs(nx-0.5)*1.72;
      var ey=(ny<0.42) ? (0.42-ny)*1.15 : (ny-0.42)*2;
      var edge=Math.max(ex,ey);
      var v=0.38+0.55*Math.pow(edge,4.5);
      /* ridges: contour bands that bend around the cursor */
      var dx=nx-mx, dy=(ny-my)*0.75, r=Math.sqrt(dx*dx+dy*dy);
      v+=0.055*Math.sin(r*24.0-nx*3.0);
      /* the cursor clears a soft opening in the field */
      v-=0.20*Math.max(0,1-r*5.0);
      if(v<0)v=0; else if(v>0.97)v=0.97;
      var on=v>((B[y&7][x&7]+0.5)/64);
      d[i]=17; d[i+1]=17; d[i+2]=17; d[i+3]=on?255:0;
    }
    }
    ctx.putImageData(img,0,0);
  }
  function schedule(){ if(!queued){queued=true; requestAnimationFrame(draw);} }
  size(); draw();
  var rt: any; var onResize=function(){clearTimeout(rt);rt=setTimeout(function(){size();draw();},180);};
  window.addEventListener('resize',onResize);
  var onMove=function(ev: PointerEvent){
    mx=ev.clientX/window.innerWidth;
    my=ev.clientY/Math.max(1,window.innerHeight);
    schedule();
  };
  window.addEventListener('pointermove',onMove,{passive:true});
  return function(){ window.removeEventListener('resize',onResize); window.removeEventListener('pointermove',onMove); };
  }

